import { useEffect, useRef, useState, type CSSProperties } from "react";
import {
  isWakeLockActive,
  requestWakeLock,
  releaseWakeLock,
  subscribeWakeLock,
  wasWakeLockRequestInvalidated,
  toggleFullScreen,
  isFullscreen,
} from "../../lib/kiosk";
import "./KioskControls.css";

interface Props {
  compact?: boolean;
  /** Host Stand opts in; existing surfaces remain manual by default. */
  autoKeepAwake?: boolean;
  responsive?: boolean;
}

export default function KioskControls({ compact = false, autoKeepAwake = false, responsive = false }: Props) {
  const [wakeLocked, setWakeLocked] = useState(isWakeLockActive());
  const [fullscreen, setFullscreen] = useState(isFullscreen());
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );
  const wakePreferred = useRef(autoKeepAwake || isWakeLockActive());

  useEffect(() => {
    let disposed = false;
    const syncWakeLock = () => setWakeLocked(isWakeLockActive());
    const requestPreferredLock = async () => {
      if (disposed || !wakePreferred.current || document.visibilityState === "hidden") return;
      const acquired = await requestWakeLock();
      // A hidden→visible cycle can race an in-flight browser prompt. The
      // hidden release invalidates that sentinel; retry once for the still-on
      // preference after the stale request has settled.
      if (!disposed && !acquired && wasWakeLockRequestInvalidated() && wakePreferred.current && !document.hidden) await requestWakeLock();
      if (disposed) return;
      syncWakeLock();
    };
    const handleVisibility = () => {
      if (document.visibilityState === "hidden") void releaseWakeLock().then(syncWakeLock);
      else void requestPreferredLock();
    };
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    const handleFullscreenChange = () => setFullscreen(isFullscreen());
    const unsubscribe = subscribeWakeLock(syncWakeLock);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("visibilitychange", handleVisibility);
    // Deferring one task keeps React Strict Mode's development mount/cleanup
    // rehearsal from cancelling the same in-flight browser permission request.
    const autoTimer = autoKeepAwake ? window.setTimeout(() => { void requestPreferredLock(); }, 0) : null;

    return () => {
      disposed = true;
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("visibilitychange", handleVisibility);
      unsubscribe();
      if (autoTimer !== null) window.clearTimeout(autoTimer);
      if (autoKeepAwake && wakePreferred.current) void releaseWakeLock();
    };
  }, [autoKeepAwake]);

  const handleToggleWakeLock = async () => {
    if (isWakeLockActive()) {
      wakePreferred.current = false;
      await releaseWakeLock();
    } else {
      wakePreferred.current = true;
      await requestWakeLock();
    }
    setWakeLocked(isWakeLockActive());
  };

  const handleToggleFullscreen = () => {
    toggleFullScreen();
    // Browsers reject fullscreen without a valid gesture/permission; the
    // fullscreenchange listener is the sole source of displayed success.
  };

  return (
    <div className={["kiosk-controls", compact && "kiosk-controls--compact", responsive && "kiosk-controls--responsive"].filter(Boolean).join(" ")} style={styles.wrap} role="group" aria-label="Device controls">
      {/* Network Status Pill */}
      <div
        className="kiosk-status-pill"
        style={{
          ...styles.pill,
          ...(responsive ? {} : { padding: "4px 8px", fontSize: 11 }),
          background: isOnline
            ? "rgba(39, 174, 96, 0.12)"
            : "rgba(224, 80, 64, 0.18)",
          borderColor: isOnline ? "rgba(39, 174, 96, 0.3)" : "rgba(224, 80, 64, 0.4)",
          color: isOnline ? "#86efac" : "#fca5a5",
        }}
        title={isOnline ? "Connected to Sentinel Cloud" : "Offline — Using Local Cache"}
      >
        <span
          style={{
            ...styles.dot,
            background: isOnline ? "#27ae60" : "#E05040",
          }}
        />
        <span className={responsive && !isOnline ? "kiosk-status-label-full" : undefined}>{isOnline ? "Live" : "Offline Cache"}</span>
        {responsive && !isOnline && <span className="kiosk-status-label-short">Offline</span>}
      </div>

      {/* Screen Keep-Awake Toggle */}
      <button
        type="button"
        style={{
          ...styles.btn,
          ...(responsive ? {} : { padding: "4px 8px", fontSize: 11 }),
          ...(wakeLocked ? styles.btnActive : {}),
        }}
        onClick={handleToggleWakeLock}
        title={wakeLocked ? "Screen lock active (stays awake)" : "Enable screen keep-awake for service"}
        className="kiosk-control-button"
        aria-label={wakeLocked ? "Keep awake on; disable screen keep-awake" : "Keep awake off; enable screen keep-awake"}
        aria-pressed={wakeLocked}
      >
        <span>{wakeLocked ? "💡" : "💤"}</span>
        {!compact && <span>{wakeLocked ? "Screen Awake" : "Keep Awake"}</span>}
      </button>

      {/* Fullscreen Kiosk Toggle */}
      <button
        type="button"
        style={{
          ...styles.btn,
          ...(fullscreen ? styles.btnActive : {}),
        }}
        onClick={handleToggleFullscreen}
        title={fullscreen ? "Exit Fullscreen" : "Enter Fullscreen Kiosk"}
        className="kiosk-control-button"
        aria-label={fullscreen ? "Fullscreen on; exit fullscreen kiosk" : "Fullscreen off; enter fullscreen kiosk"}
        aria-pressed={fullscreen}
      >
        <span>⛶</span>
        {!compact && <span>{fullscreen ? "Exit Fullscreen" : "Kiosk"}</span>}
      </button>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  wrap: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    flexShrink: 0,
  },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    borderRadius: 6,
    fontWeight: 700,
    border: "1px solid transparent",
    whiteSpace: "nowrap",
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    display: "inline-block",
  },
  btn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    background: "rgba(255, 255, 255, 0.04)",
    border: "1px solid var(--border, rgba(255, 255, 255, 0.08))",
    borderRadius: 6,
    color: "var(--muted, #8A7A66)",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.15s ease",
  },
  btnActive: {
    background: "rgba(249, 175, 54, 0.12)",
    borderColor: "var(--gold, #F9AF36)",
    color: "var(--gold, #F9AF36)",
    fontWeight: 700,
  },
};
